import {
  createAuthenticationError,
  resolveAuthenticationGuidanceFromError,
  resolveAuthenticationReasonFromState,
} from "../auth/auth-guidance";
import { formatInlineWakeLoopCliCommands } from "../runtime/cli-entry-command";
import { getAuthSessionState } from "./auth-session";
import {
  clearSelectedWakeLoopProfileId,
  getSelectedWakeLoopProfileId,
  setSelectedWakeLoopProfileId,
} from "./wakeloop-profile-selection";
import {
  resolveWakeLoopProfile,
  resolveWakeLoopProfileByIdOrName,
  type WakeLoopProfile,
  type WakeLoopProfileKind,
} from "./wakeloop-profiles-api";

export type ResolveWakeLoopProfileInput = {
  wakeLoopProfileId?: string;
  profileKind?: WakeLoopProfileKind;
  allowUnauthenticated?: boolean;
  persistSelection?: boolean;
  allowDefaultFallback?: boolean;
  authAction?: string;
};

const PROFILE_REQUIRED_ERROR_ROOT =
  "no WakeLoop profile is selected for this terminal session yet";
const PROFILE_REQUIRED_ERROR_MESSAGE = `${PROFILE_REQUIRED_ERROR_ROOT}; run \`wakeloop start\` to choose one, or pass \`--profile <id>\`.`;
const SERVICE_TRACE_PROFILE_REQUIRED_ERROR_MESSAGE = `${PROFILE_REQUIRED_ERROR_ROOT}; dispatch trace needs an acting profile. Run \`wakeloop profile set <profile-id>\` once, or pass \`--profile <profile-id>\` with this trace command. \`--target-profile\` only identifies the agent being traced.`;
const PROFILE_REQUIRED_ERROR_PREFIX =
  "no WakeLoop profile is selected for this terminal session yet; run `";
const PROFILE_REQUIRED_ERROR_SUFFIX =
  " start` to choose one, or pass `--profile <id>`.";

export function isSelectedWakeLoopProfileRequiredError(
  error: unknown
): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  if (error.message === PROFILE_REQUIRED_ERROR_MESSAGE) {
    return true;
  }

  if (
    error.message.includes(PROFILE_REQUIRED_ERROR_ROOT) &&
    error.message.includes("--profile <")
  ) {
    return true;
  }

  return (
    error.message.startsWith(PROFILE_REQUIRED_ERROR_PREFIX) &&
    error.message.endsWith(PROFILE_REQUIRED_ERROR_SUFFIX)
  );
}

export async function resolveWakeLoopProfileOrNull(
  input: ResolveWakeLoopProfileInput = {}
): Promise<WakeLoopProfile | null> {
  const explicitWakeLoopProfileId = normalizeOptionalString(
    input.wakeLoopProfileId
  );
  if (explicitWakeLoopProfileId) {
    return await resolveExplicitWakeLoopProfile(explicitWakeLoopProfileId, {
      profileKind: input.profileKind,
      allowUnauthenticated: input.allowUnauthenticated === true,
      persistSelection: input.persistSelection !== false,
    });
  }

  const selectedWakeLoopProfileId = await getSelectedWakeLoopProfileId();
  if (selectedWakeLoopProfileId) {
    const selectedProfile = await resolveSelectedWakeLoopProfile(
      selectedWakeLoopProfileId,
      {
        profileKind: input.profileKind,
        allowUnauthenticated: input.allowUnauthenticated === true,
      }
    );
    if (selectedProfile) {
      return selectedProfile;
    }
  }

  if (input.allowUnauthenticated === true) {
    const authState = await getAuthSessionState({
      validateRemote: false,
    });
    if (authState.state !== "valid") {
      return null;
    }
  }

  if (input.allowDefaultFallback !== true) {
    return null;
  }

  let fallbackProfile: WakeLoopProfile;
  try {
    fallbackProfile = await resolveWakeLoopProfile({
      profileKind: input.profileKind,
    });
  } catch (error) {
    if (input.allowUnauthenticated === true && isAuthRequiredError(error)) {
      return null;
    }
    throw error;
  }
  if (input.persistSelection !== false) {
    await setSelectedWakeLoopProfileId(fallbackProfile.wakeLoopProfileId);
  }
  return fallbackProfile;
}

async function resolveExplicitWakeLoopProfile(
  wakeLoopProfileId: string,
  input: {
    profileKind?: WakeLoopProfileKind;
    allowUnauthenticated: boolean;
    persistSelection: boolean;
  }
): Promise<WakeLoopProfile | null> {
  try {
    const profile = await resolveWakeLoopProfileByIdOrName({
      profileIdentifier: wakeLoopProfileId,
      profileKind: input.profileKind,
    });
    if (input.persistSelection) {
      await setSelectedWakeLoopProfileId(profile.wakeLoopProfileId);
    }
    return profile;
  } catch (error) {
    if (isAuthRequiredError(error) && input.allowUnauthenticated) {
      return null;
    }
    throw error;
  }
}

export async function requireWakeLoopProfile(
  input: ResolveWakeLoopProfileInput = {}
): Promise<WakeLoopProfile> {
  const profile = await resolveWakeLoopProfileOrNull(input);
  if (profile) {
    return profile;
  }

  const authState = await getAuthSessionState({
    validateRemote: false,
  });
  if (authState.state === "valid") {
    throw new Error(
      formatInlineWakeLoopCliCommands(
        resolveProfileRequiredErrorMessage(input.authAction)
      )
    );
  }
  const reason = resolveAuthenticationReasonFromState(authState.state);
  throw createAuthenticationError({
    reason: reason ?? "required",
    action: input.authAction,
  });
}

function resolveProfileRequiredErrorMessage(authAction?: string): string {
  if (authAction === "reading a dispatch trace") {
    return SERVICE_TRACE_PROFILE_REQUIRED_ERROR_MESSAGE;
  }

  return PROFILE_REQUIRED_ERROR_MESSAGE;
}

async function resolveSelectedWakeLoopProfile(
  selectedWakeLoopProfileId: string,
  input: {
    profileKind?: WakeLoopProfileKind;
    allowUnauthenticated: boolean;
  }
): Promise<WakeLoopProfile | null> {
  try {
    return await resolveWakeLoopProfile({
      wakeLoopProfileId: selectedWakeLoopProfileId,
      profileKind: input.profileKind,
    });
  } catch (error) {
    if (isAuthRequiredError(error)) {
      if (input.allowUnauthenticated) {
        return null;
      }
      throw error;
    }

    if (isProfileMissingError(error)) {
      await clearSelectedWakeLoopProfileId();
      return null;
    }

    throw error;
  }
}

function normalizeOptionalString(value: unknown): string | null {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
}

function isAuthRequiredError(error: unknown): boolean {
  return resolveAuthenticationGuidanceFromError(error) !== null;
}

function isProfileMissingError(error: unknown): boolean {
  const code =
    error && typeof error === "object" && "code" in error
      ? String((error as { code?: unknown }).code ?? "")
      : "";
  if (code === "profile.not_found") {
    return true;
  }

  const message = error instanceof Error ? error.message : String(error ?? "");
  return message.includes("profile not found");
}
